import React, { useMemo } from 'react';
import { useAppSelector } from '../../../store/hooks';
import { OrderItem, CustomerType, DeliveryType } from '../../../types/order';
import { calculateItemPricing, calculateDeliveryFee, calculateOrderTotals } from '../../../utils/pricingUtils';

interface LivePricingPreviewProps {
  items: OrderItem[];
  customerType: CustomerType;
  deliveryType: DeliveryType;
  city: string;
}

const LivePricingPreview: React.FC<LivePricingPreviewProps> = ({ items, customerType, deliveryType, city }) => {
  const pricingState = useAppSelector(state => state.pricing);

  // Calculate pricing in real-time
  const pricing = useMemo(() => {
    // Filter out items without recipes or quantities
    const validItems = items.filter(item => item.recipeId && item.quantity > 0);

    if (validItems.length === 0) {
      return {
        itemPrices: [],
        subtotal: 0,
        deliveryFee: 0,
        total: 0,
        hasItems: false
      };
    }

    // Calculate item pricing
    const itemPrices = calculateItemPricing(validItems, customerType, pricingState);

    // Calculate delivery fee (without distance since we don't have geocoding yet)
    const { deliveryFee, deliveryZoneId } = calculateDeliveryFee(
      deliveryType,
      city,
      undefined, // distanceFromHome not available during form entry
      undefined, // manualDistance not available during form entry
      pricingState
    );

    // Calculate totals
    const { subtotal, total } = calculateOrderTotals(itemPrices, deliveryFee);

    return {
      itemPrices,
      subtotal,
      deliveryFee,
      deliveryZoneId,
      total,
      hasItems: true
    };
  }, [items, customerType, deliveryType, city, pricingState]);

  // Calculate total quantity for deal display
  const totalQuantity = useMemo(() => {
    return items.reduce((sum, item) => sum + (item.recipeId ? item.quantity : 0), 0);
  }, [items]);

  // Check if a deal is applied for Individual customers
  const appliedDeal = useMemo(() => {
    if (customerType === 'Individual' && totalQuantity > 0) {
      return pricingState.deals.find(deal => deal.quantity === totalQuantity);
    }
    return undefined;
  }, [customerType, totalQuantity, pricingState.deals]);

  // If no items, show minimal preview
  if (!pricing.hasItems) {
    return (
      <div style={styles.container}>
        <h3 style={styles.title}>Pricing Preview</h3>
        <div style={styles.emptyState}>Add items to see pricing</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Pricing Preview</h3>

      {/* Item Lines */}
      <div style={styles.itemsSection}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Item</th>
              <th style={{ ...styles.tableHeader, textAlign: 'right' }}>Qty</th>
              <th style={{ ...styles.tableHeader, textAlign: 'right' }}>Price/Dz</th>
              <th style={{ ...styles.tableHeader, textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {pricing.itemPrices.map((itemPrice, index) => {
              const orderItem = items.find(i => i.recipeId === itemPrice.recipeId);
              return (
                <tr key={index} style={styles.tableRow}>
                  <td style={styles.tableCell}>{orderItem?.recipeName || 'Unknown'}</td>
                  <td style={{ ...styles.tableCell, textAlign: 'right' }}>{itemPrice.quantity}</td>
                  <td style={{ ...styles.tableCell, textAlign: 'right' }}>${itemPrice.pricePerDozen.toFixed(2)}</td>
                  <td style={{ ...styles.tableCell, textAlign: 'right' }}>
                    ${(itemPrice.quantity * itemPrice.pricePerDozen).toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Deal Info for Individual customers */}
      {customerType === 'Individual' && appliedDeal && (
        <div style={styles.dealBadge}>
          Deal Applied: {totalQuantity} dozen @ ${appliedDeal.price}/dz
        </div>
      )}

      {/* Pricing Tiers Info for Retailers */}
      {customerType === 'Retailer' && (
        <div style={styles.infoBadge}>
          {totalQuantity >= 50
            ? `Tier: 50+ dozen ($${pricingState.retailerPrice50Plus}/dz)`
            : `Tier: Under 50 dozen ($${pricingState.retailerPriceUnder50}/dz)`}
        </div>
      )}

      {/* Totals */}
      <div style={styles.totalsSection}>
        <div style={styles.totalRow}>
          <span style={styles.totalLabel}>Subtotal:</span>
          <span style={styles.totalValue}>${pricing.subtotal.toFixed(2)}</span>
        </div>

        <div style={styles.totalRow}>
          <span style={styles.totalLabel}>
            Delivery Fee:
            {deliveryType === 'Pickup' && ' (Pickup)'}
            {deliveryType === 'Delivery' && !city && ' (Enter city)'}
            {deliveryType === 'Delivery' && city && !pricing.deliveryZoneId && ' (City-based)'}
          </span>
          <span style={styles.totalValue}>${pricing.deliveryFee.toFixed(2)}</span>
        </div>

        {deliveryType === 'Delivery' && city && pricing.deliveryFee === 0 && (
          <div style={styles.deliveryNote}>
            Note: Delivery fee will be calculated based on distance after address is geocoded
          </div>
        )}

        <div style={{ ...styles.totalRow, ...styles.finalTotal }}>
          <span style={styles.finalTotalLabel}>Total:</span>
          <span style={styles.finalTotalValue}>${pricing.total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '24px',
    border: '1px solid #dee2e6'
  },
  title: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginTop: 0,
    marginBottom: '16px'
  },
  emptyState: {
    textAlign: 'center',
    color: '#999',
    padding: '20px',
    fontSize: '14px'
  },
  itemsSection: {
    marginBottom: '16px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  tableHeader: {
    padding: '8px',
    borderBottom: '2px solid #dee2e6',
    color: '#555',
    fontWeight: '600',
    textAlign: 'left'
  },
  tableRow: {
    borderBottom: '1px solid #e9ecef'
  },
  tableCell: {
    padding: '8px',
    color: '#333'
  },
  dealBadge: {
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '12px',
    border: '1px solid #c3e6cb',
    textAlign: 'center'
  },
  infoBadge: {
    backgroundColor: '#d1ecf1',
    color: '#0c5460',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '12px',
    border: '1px solid #bee5eb',
    textAlign: 'center'
  },
  totalsSection: {
    borderTop: '2px solid #dee2e6',
    paddingTop: '12px'
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '14px'
  },
  totalLabel: {
    color: '#555',
    fontWeight: '500'
  },
  totalValue: {
    color: '#333',
    fontWeight: '600'
  },
  deliveryNote: {
    fontSize: '12px',
    color: '#856404',
    backgroundColor: '#fff3cd',
    padding: '8px',
    borderRadius: '4px',
    marginBottom: '8px',
    border: '1px solid #ffeeba'
  },
  finalTotal: {
    marginTop: '8px',
    paddingTop: '12px',
    borderTop: '2px solid #dee2e6'
  },
  finalTotalLabel: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#333'
  },
  finalTotalValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#28a745'
  }
};

export default LivePricingPreview;
